import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { createContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import assignmentService from 'services/assignment';
import batchService from 'services/batch';
import { EXAMPLE_ASSIGNMENT_ID } from 'utils/constants';
import { exportSubmissionsToCSV } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, assignments, currentAssignment, submissions, setSubmissions, setBatch } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { setJoyridePaused } = useJoyride();
    const location = useLocation();

    const [viewBaselineDialog, setViewBaselineDialog] = useState({ open: false });
    const [viewContentBankResourcesDialog, setViewContentBankResourcesDialog] = useState({ open: false });
    const [viewGradingSettingsDialog, setViewGradingSettingsDialog] = useState({ open: false });
    const [uploadAssignmentsDialog, setUploadAssignmentsDialog] = useState({ open: false });
    const [debugDialog, setDebugDialog] = useState({ open: false });

    const createSubmissions = async (submissions) => {
        const timestamp = new Date().getTime();
        const id = uuid();
        const batch = {
            id,
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.id,
            course_id: course.id,
            assignment_id: currentAssignment.id,
            files: submissions,
            is_finished: false,
        };
        setBatch((current) => [...(current || []), batch]);
        await batchService.createSubmissions(id, batch);
    };

    const exportSubmissions = () => {
        return exportSubmissionsToCSV(course, currentAssignment, submissions);
    };

    const resetSubmissions = () => {
        setDialog({
            open: true,
            message: 'Erase Existing Assignment Data?',
            description:
                'If you reset, any existing submissions and associated grades/feedback will be permanently deleted.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                setBatch([]);
                setSubmissions([]);
                closeDialog();
                await Promise.all([
                    await assignmentService.deleteAssignmentSubmissions(currentAssignment.id),
                    await assignmentService.deleteAssignmentBatch(currentAssignment.id),
                ]);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    useEffect(() => {
        if (!assignments) return;
        if (
            location.pathname.includes(EXAMPLE_ASSIGNMENT_ID) &&
            !assignments.some(({ id }) => id === EXAMPLE_ASSIGNMENT_ID)
        ) {
            history.replace('/assignments');
        }
    }, [location, assignments, history]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => {
            setJoyridePaused(true);
        };
    }, [setJoyridePaused]);

    return (
        <PageContext.Provider
            value={{
                assignment: currentAssignment,
                viewBaselineDialog,
                setViewBaselineDialog,
                viewContentBankResourcesDialog,
                setViewContentBankResourcesDialog,
                viewGradingSettingsDialog,
                setViewGradingSettingsDialog,
                uploadAssignmentsDialog,
                setUploadAssignmentsDialog,
                debugDialog,
                setDebugDialog,
                createSubmissions,
                exportSubmissions,
                resetSubmissions,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
